import { iconAlertaAtencao, iconDocDocumento, iconNavUpload, iconSetaDuplaEsquerda } from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsIcon, BsdsInput } from '@bsds-lib-component/react';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

import { Button, Dropdown, StepperX } from '../../../../components';
import { formatDocSubestipulante, type UseInclusionProponentImportReturn } from '../../../../hooks';
import { useVidaProtocolAtendimentoStore } from '../../../../store/vida/vidaProtocolServiceStore';
import type { DropdownOption } from '../../../../types/componentType';
import type { ListaSubestipulanteItem } from '../../../../types/vida/subEstipulante/subEstipulanteType';
import {
  buildAutoFieldMapping,
  buildBatchProponentItemsFromSpreadsheet,
  buildImportProponentBatchRequest,
  extractFileColumnOptions,
  FALLBACK_LAYOUT_FIELDS,
  getActiveLayoutFields,
  isCsvFile,
  mapEmissionTypeToMovtoManut,
  mapUiLayoutTypeToImportCode,
  parseLayoutImportFields,
  parseSpreadsheetRows,
  type LayoutFieldDefinition,
} from '../../../../utils/inclusionProponentUtil';

const IMPORT_TOTAL_STEPS = 3;

const IMPORT_STEP_LABELS: Record<number, string> = {
  1: 'Seleção do arquivo e layout',
  2: 'Vincular campos e importar',
  3: 'Resultado da importação',
};

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

const ACCEPTED_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
];

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

type ImportStep = 1 | 2 | 3;

type EmissionType = 'normal' | 'complementar';
type ImportType = 'total' | 'adicional';
type LayoutType = 'completo' | 'reduzido';

type ImportErrorItem = {
  linha: number;
  campo: string;
  critica: string;
};

type ImportResult =
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string; errors?: ImportErrorItem[] };

const LAYOUT_TYPE_OPTIONS: DropdownOption[] = [
  { label: 'Completo', value: 'completo' },
  { label: 'Reduzido', value: 'reduzido' },
];

const EMISSION_TYPE_OPTIONS: DropdownOption[] = [
  { label: 'Normal', value: 'normal' },
  { label: 'Complementar', value: 'complementar' },
];

const IMPORT_TYPE_OPTIONS: DropdownOption[] = [
  { label: 'Total', value: 'total' },
  { label: 'Adicional', value: 'adicional' },
];

const PREVIEW_MAX_LENGTH = 15;

const truncatePreview = (text: string): string => {
  if (!text) return '—';

  if (text.length <= PREVIEW_MAX_LENGTH) return text;

  return `${text.slice(0, PREVIEW_MAX_LENGTH)}…`;
};

const buildDefaultFieldMapping = (
  layoutFields: LayoutFieldDefinition[],
  fileColumns: string[],
): Record<string, string> => buildAutoFieldMapping(layoutFields, fileColumns);

const buildEmptyFieldMapping = (layoutFields: LayoutFieldDefinition[]): Record<string, string> =>
  layoutFields.reduce(
    (acc, { key }) => {
      acc[key] = '';
      return acc;
    },
    {} as Record<string, string>,
  );

const getLayoutTypeLabel = (layoutType: LayoutType): string =>
  layoutType === 'completo' ? 'Completo' : 'Reduzido';

type ImportConfigState = {
  delimiter: string;
  headerLines: string;
  footerLines: string;
  emissionType: EmissionType | null;
  competencia: string;
  importType: ImportType | null;
};

const INITIAL_IMPORT_CONFIG: ImportConfigState = {
  delimiter: '',
  headerLines: '',
  footerLines: '',
  emissionType: null,
  competencia: '',
  importType: null,
};

const buildCompetenciaPlaceholderOptions = (): DropdownOption[] => {
  const options: DropdownOption[] = [];
  const now = new Date();

  for (let i = 0; i < 24; i += 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const value = `${month}-${year}`;

    options.push({
      label: `${month}/${year}`,
      value,
    });
  }

  return options;
};

const isAcceptedFile = (file: File): boolean => {
  const lowerName = file.name.toLowerCase();

  const hasValidExtension = ACCEPTED_EXTENSIONS.some((ext) => lowerName.endsWith(ext));

  const hasValidMime = ACCEPTED_MIME_TYPES.includes(file.type);

  return hasValidExtension || hasValidMime;
};

const handleBsdsInputChange =
  (setter: (value: string) => void) => (e: React.FormEvent<HTMLBsdsInputElement>) => {
    const target = e.target as HTMLBsdsInputElement;

    const value =
      (e as React.FormEvent<HTMLBsdsInputElement> & {
        detail?: { value: string };
      }).detail?.value ||
      target?.value ||
      '';

    setter(String(value));
  };

type ImportProponentesBatchSectionProps = {
  onCancel: () => void;
  onImportSuccess?: () => void;
  subestipulante?: ListaSubestipulanteItem | null;
  proponentImport: UseInclusionProponentImportReturn;
};

const ImportProponentesBatchSection: React.FC<ImportProponentesBatchSectionProps> = ({
  onCancel,
  onImportSuccess,
  subestipulante = null,
  proponentImport,
}) => {
  const fileInputId = useId();
  const dropZoneId = useId();
  const layoutTypeDropdownId = useId();
  const emissionTypeDropdownId = useId();
  const competenciaDropdownId = useId();
  const importTypeDropdownId = useId();
  const mappingSectionId = useId();
  const resultsSectionId = useId();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const attendanceProtocol = useVidaProtocolAtendimentoStore((state) => state.attendanceProtocol);

  const [currentStep, setCurrentStep] = useState<ImportStep>(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [layoutType, setLayoutType] = useState<LayoutType | null>(null);
  const [importConfig, setImportConfig] =
    useState<ImportConfigState>(INITIAL_IMPORT_CONFIG);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [fileColumns, setFileColumns] = useState<string[]>([]);
  const [fileDataRows, setFileDataRows] = useState<string[][]>([]);
  const [filePreviewRow, setFilePreviewRow] = useState<Record<string, string>>({});
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const layoutFields = useMemo(() => {
    const apiFields = parseLayoutImportFields(proponentImport.layoutRetorno?.layouts);
    const baseFields = apiFields ?? FALLBACK_LAYOUT_FIELDS;
    return layoutType ? getActiveLayoutFields(layoutType, baseFields) : baseFields;
  }, [layoutType, proponentImport.layoutRetorno?.layouts]);

  const fileColumnOptions = useMemo<DropdownOption[]>(
    () => fileColumns.map((column) => ({ label: column, value: column })),
    [fileColumns],
  );

  const competenciaOptions = useMemo(
    () => buildCompetenciaPlaceholderOptions(),
    [],
  );

  const activeLayoutFields = useMemo(
    () => (layoutType ? getActiveLayoutFields(layoutType, layoutFields) : []),
    [layoutType, layoutFields],
  );

  useEffect(() => {
    if (!layoutType || !subestipulante) {
      return;
    }

    void proponentImport.loadLayoutImport(subestipulante, layoutType);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recarrega apenas ao mudar layout/subestipulante
  }, [layoutType, subestipulante]);

  const isImportConfigValid =
    importConfig.delimiter.trim() !== '' &&
    importConfig.competencia.trim() !== '' &&
    importConfig.emissionType !== null &&
    importConfig.importType !== null;

  const isStepOneComplete =
    !!selectedFile && !!layoutType && isImportConfigValid;

  const isFieldMappingValid = activeLayoutFields.every(
    ({ key }) => fieldMapping[key]?.trim() !== '',
  );

  const parseSelectedFile = async (file: File): Promise<void> => {
    if (!isCsvFile(file)) {
      throw new Error('A importação suporta apenas arquivos CSV no momento.');
    }

    const content = await file.text();
    const headerLineCount = Number(importConfig.headerLines) || 0;
    const footerLineCount = Number(importConfig.footerLines) || 0;
    const rows = parseSpreadsheetRows(
      content,
      importConfig.delimiter,
      headerLineCount,
      footerLineCount,
    );
    const { columns, dataRows, previewRow } = extractFileColumnOptions(
      rows,
      headerLineCount > 0,
    );

    if (columns.length === 0) {
      throw new Error('Não foi possível identificar colunas no arquivo selecionado.');
    }

    setFileColumns(columns);
    setFileDataRows(dataRows);
    setFilePreviewRow(previewRow);
    setFieldMapping(buildDefaultFieldMapping(activeLayoutFields, columns));
  };

  const handleLayoutTypeChange = (value: LayoutType) => {
    setLayoutType(value);
    setFieldMapping(buildEmptyFieldMapping(getActiveLayoutFields(value, layoutFields)));
    setFileColumns([]);
    setFileDataRows([]);
    setFilePreviewRow({});
    setFileError(null);
    setImportResult(null);
    setCurrentStep(1);
    proponentImport.clearLayoutError();
  };

  const handleFileSelection = (file: File | null) => {
    if (!file) {
      setSelectedFile(null);
      setFileError(null);
      setImportResult(null);
      setCurrentStep(1);
      return;
    }

    if (!layoutType) {
      setFileError('Selecione o tipo de layout antes de escolher o arquivo.');
      return;
    }

    if (!isAcceptedFile(file)) {
      setSelectedFile(null);
      setFileError('Formato inválido. Utilize arquivos XLSX, XLS ou CSV.');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSelectedFile(null);
      setFileError('O arquivo excede o tamanho máximo de 20 MB.');
      return;
    }

    setSelectedFile(file);
    setFileError(null);
    setImportResult(null);
    setCurrentStep(1);
    setFileColumns([]);
    setFileDataRows([]);
    setFilePreviewRow({});
    setFieldMapping(buildEmptyFieldMapping(getActiveLayoutFields(layoutType, layoutFields)));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    handleFileSelection(file);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files?.[0] ?? null;
    handleFileSelection(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const openFilePicker = () => {
    if (!layoutType) {
      setFileError('Selecione o tipo de layout antes de escolher o arquivo.');
      return;
    }

    fileInputRef.current?.click();
  };

  const handleCancelFileSelection = () => {
    setSelectedFile(null);
    setImportResult(null);
    setCurrentStep(1);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleContinueToStepTwo = async () => {
    if (!isStepOneComplete || !layoutType || !selectedFile) return;

    setFileError(null);

    try {
      await parseSelectedFile(selectedFile);
      setCurrentStep(2);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao ler o arquivo selecionado.';
      setFileError(message);
    }
  };

  const handleBackToStepOne = () => {
    setCurrentStep(1);
  };

  const handleImportPlanilha = async () => {
    if (
      !isStepOneComplete
      || !selectedFile
      || !isFieldMappingValid
      || !layoutType
      || !subestipulante
      || !importConfig.emissionType
    ) {
      return;
    }

    proponentImport.clearImportError();

    try {
      const headerLineCount = Number(importConfig.headerLines) || 0;
      const jsonProponente = buildBatchProponentItemsFromSpreadsheet({
        dataRows: fileDataRows,
        columns: fileColumns,
        fieldMapping,
        headerLineCount,
      });

      if (jsonProponente.length === 0) {
        throw new Error('Nenhum registro válido encontrado na planilha.');
      }

      const request = buildImportProponentBatchRequest({
        identificadorSubestipulante: subestipulante.identSubestipulanteOut,
        codigoUsuarioInclusao: proponentImport.resolveUsuarioInclusao(),
        layoutImportCode: mapUiLayoutTypeToImportCode(layoutType),
        cTpoMovtoManut: mapEmissionTypeToMovtoManut(importConfig.emissionType),
        protocoloId: attendanceProtocol?.idProtocolo ?? 0,
        jsonProponente,
      });

      const message = await proponentImport.importProponentBatch(request);

      setImportResult({ kind: 'success', message });
      setCurrentStep(3);
      onImportSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao importar a planilha.';
      setImportResult({
        kind: 'error',
        message,
      });
      setCurrentStep(3);
    }
  };

  const handleResetImport = () => {
    setCurrentStep(1);
    setSelectedFile(null);
    setFileError(null);
    setLayoutType(null);
    setImportConfig(INITIAL_IMPORT_CONFIG);
    setFieldMapping({});
    setFileColumns([]);
    setFileDataRows([]);
    setFilePreviewRow({});
    setImportResult(null);
    proponentImport.clearLayoutError();
    proponentImport.clearImportError();

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const updateFieldMapping = (field: string, value: string) => {
    setFieldMapping((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getFieldPreview = (mappedColumn: string): string => {
    if (!mappedColumn) return '—';

    const preview = filePreviewRow[mappedColumn] ?? '—';
    return truncatePreview(preview);
  };

  const updateImportConfig = <
    K extends keyof ImportConfigState,
  >(
    key: K,
    value: ImportConfigState[K],
  ) => {
    setImportConfig((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const renderStepOne = () => (
    <>
      <div
        className="bsds-mb-24 pb-6"
        role="region"
        aria-label="Configuração da importação"
      >
        <div
          id={dropZoneId}
          className={`bsds-display-flex flex-col bsds-align-items-center bsds-justify-content-center bsds-p-24 bsds-mb-24 rounded-md transition-colors ${!layoutType
              ? 'bg-gray-100/60 opacity-60 pointer-events-none'
              : isDragOver
                ? 'bg-primary-50 border border-primary-400'
                : 'bg-gray-50/50'
            }`}
          onDrop={layoutType ? handleDrop : undefined}
          onDragOver={layoutType ? handleDragOver : undefined}
          onDragLeave={layoutType ? handleDragLeave : undefined}
          aria-label="Área para arrastar planilha"
          aria-disabled={!layoutType}
        >
          <BsdsIcon
            icon={iconDocDocumento}
            className="w-10 h-10 text-gray-400 bsds-mb-12"
            aria-hidden="true"
          />

          <p className="bsds-text-regular font-semibold text-gray-800 bsds-mb-4 text-center">
            {selectedFile ? 'Planilha selecionada' : 'Arraste a planilha aqui'}
          </p>

          <p className="bsds-text-regular text-sm text-gray-500 bsds-mb-16 text-center">
            {selectedFile
              ? selectedFile.name
              : 'ou clique para selecionar um arquivo'}
          </p>

          <Button
            type="button"
            color="primary"
            fill="outline"
            icon={iconNavUpload}
            iconPosition="start"
            onClick={openFilePicker}
            disabled={!layoutType}
            aria-label={
              selectedFile
                ? 'Selecionar outro arquivo da planilha'
                : 'Selecionar arquivo da planilha'
            }
          >
            {selectedFile ? 'Selecionar outro arquivo' : 'Selecionar arquivo'}
          </Button>

          {!selectedFile && (
            <p className="bsds-text-regular text-sm text-gray-500 bsds-mt-16 text-center">
              Nenhum arquivo selecionado
            </p>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bsds-mb-24">
          <BsdsInput
            type="text"
            label="Delimitador"
            placeholder="Informe um delimitador"
            value={importConfig.delimiter}
            onInput={handleBsdsInputChange((value) =>
              updateImportConfig('delimiter', value),
            )}
            platform="web"
            required
            aria-label="Delimitador do arquivo"
          />

          <BsdsInput
            type="text"
            inputMode="numeric"
            label="Qtd de linhas no cabeçalho"
            placeholder="Quantidade de linhas no cabeçalho"
            value={importConfig.headerLines}
            onInput={handleBsdsInputChange((value) =>
              updateImportConfig('headerLines', value.replace(/\D/g, '')),
            )}
            platform="web"
            aria-label="Quantidade de linhas de cabeçalho"
          />

          <BsdsInput
            type="text"
            inputMode="numeric"
            label="Qtd de linhas no rodapé"
            placeholder="Quantidade de linhas no rodapé"
            value={importConfig.footerLines}
            onInput={handleBsdsInputChange((value) =>
              updateImportConfig('footerLines', value.replace(/\D/g, '')),
            )}
            platform="web"
            aria-label="Quantidade de linhas de rodapé"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bsds-mb-24">
          <Dropdown
            id={layoutTypeDropdownId}
            label="Tipo de layout"
            placeholder="Selecione o tipo de layout"
            value={layoutType ?? ''}
            onChange={(value) => {
              if (value === 'completo' || value === 'reduzido') {
                handleLayoutTypeChange(value);
                return;
              }

              setLayoutType(null);
              setFieldMapping(buildEmptyFieldMapping(FALLBACK_LAYOUT_FIELDS));
              setFileColumns([]);
              setFileDataRows([]);
              setFilePreviewRow({});
              setFileError(null);
              setImportResult(null);
              setCurrentStep(1);
              proponentImport.clearLayoutError();
            }}
            options={LAYOUT_TYPE_OPTIONS}
            required
          />

          <Dropdown
            id={emissionTypeDropdownId}
            label="Tipo de emissão"
            placeholder="Selecione o tipo de emissão"
            value={importConfig.emissionType ?? ''}
            onChange={(value) =>
              updateImportConfig(
                'emissionType',
                value === '' ? null : (value as EmissionType),
              )
            }
            options={EMISSION_TYPE_OPTIONS}
            required
          />

          <Dropdown
            id={competenciaDropdownId}
            label="Competência"
            placeholder="Selecione a competência"
            value={importConfig.competencia}
            onChange={(value) =>
              updateImportConfig('competencia', value)
            }
            options={competenciaOptions}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Dropdown
            id={importTypeDropdownId}
            label="Tipo de importação"
            placeholder="Selecione o tipo de importação"
            value={importConfig.importType ?? ''}
            onChange={(value) =>
              updateImportConfig(
                'importType',
                value === '' ? null : (value as ImportType),
              )
            }
            options={IMPORT_TYPE_OPTIONS}
            required
          />
        </div>
      </div>

      {proponentImport.layoutError && (
        <p className="bsds-text-regular text-sm text-amber-700 bsds-mt-12" role="status">
          {proponentImport.layoutError} Utilizando layout padrão.
        </p>
      )}

      {proponentImport.isLoadingLayout && (
        <p className="bsds-text-regular text-sm text-gray-600 bsds-mt-12" role="status">
          Carregando layout de importação...
        </p>
      )}

      {fileError && (
        <p
          className="bsds-text-regular text-sm text-red-600 bsds-mt-12"
          role="alert"
        >
          {fileError}
        </p>
      )}

      <div className="bsds-display-flex flex-col sm:flex-row gap-4 bsds-mt-24 bsds-pt-8">
        {selectedFile && (
          <Button
            type="button"
            color="secondary"
            fill="outline"
            className="bsds-width-100 sm:w-auto"
            onClick={handleCancelFileSelection}
            aria-label="Cancelar seleção do arquivo"
          >
            Cancelar arquivo
          </Button>
        )}

        <Button
          type="button"
          color="primary"
          fill="solid"
          className="bsds-width-100 sm:w-auto sm:ml-auto"
          onClick={handleContinueToStepTwo}
          disabled={!isStepOneComplete}
          aria-label="Continuar para vincular campos"
        >
          Continuar
        </Button>
      </div>
    </>
  );

  const renderStepTwo = () => (
    <>
      <div
        className="bsds-display-flex bsds-align-items-center gap-3 bsds-mb-24 pb-4 border-b border-gray-100"
        role="region"
        aria-label="Resumo do arquivo selecionado"
      >
        <BsdsIcon
          icon={iconDocDocumento}
          className="w-8 h-8 text-primary-500 shrink-0"
          aria-hidden="true"
        />

        <div className="min-w-0">
          <p
            className="bsds-text-regular text-sm font-semibold text-gray-800 truncate"
            title={selectedFile?.name}
          >
            {selectedFile?.name}
          </p>

          <p className="bsds-text-regular text-sm text-gray-500 bsds-mt-4">
            Layout: {layoutType ? getLayoutTypeLabel(layoutType) : '—'}
          </p>
        </div>
      </div>

      <div
        id={mappingSectionId}
        role="region"
        aria-label="Vincular campos da planilha"
      >
        <span className="text-sm font-semibold text-gray-800 bsds-display-block bsds-mb-12">
          Vincular campos da planilha
        </span>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bsds-mb-12 pb-2 border-b border-gray-100">
          <span className="text-sm font-semibold text-gray-800">Layout</span>
          <span className="text-sm font-semibold text-gray-800">Campo Ativo</span>
          <span className="text-sm font-semibold text-gray-800">Visualizar</span>
        </div>

        <ul className="list-none p-0 m-0 divide-y divide-gray-100/50" role="list">
          {activeLayoutFields.map(({ key, label }) => {
            const mappingDropdownId = `${competenciaDropdownId}-mapping-${key}`;

            return (
              <li
                key={key}
                className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center py-4"
                role="listitem"
              >
                <span className="text-sm text-gray-800">{label}</span>

                <Dropdown
                  id={mappingDropdownId}
                  placeholder="Selecione o campo"
                  value={fieldMapping[key]}
                  onChange={(value) => updateFieldMapping(key, value)}
                  options={fileColumnOptions}
                  required
                />

                <span
                  className="text-sm text-gray-600 font-mono truncate"
                  title={getFieldPreview(fieldMapping[key])}
                  aria-label={`Pré-visualização de ${label}`}
                >
                  {getFieldPreview(fieldMapping[key])}
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="bsds-display-flex flex-col sm:flex-row gap-4 bsds-mt-24 bsds-pt-8">
        <Button
          type="button"
          color="secondary"
          fill="outline"
          className="bsds-width-100 sm:w-auto"
          onClick={handleBackToStepOne}
          disabled={proponentImport.isImporting}
          aria-label="Voltar para seleção do arquivo"
        >
          Voltar
        </Button>

        <Button
          type="button"
          color="primary"
          fill="solid"
          className="bsds-width-100 sm:w-auto sm:ml-auto"
          onClick={() => void handleImportPlanilha()}
          disabled={!isFieldMappingValid || proponentImport.isImporting}
          aria-label="Confirmar importação da planilha"
        >
          {proponentImport.isImporting ? 'Importando...' : 'Importar Planilha'}
        </Button>
      </div>
    </>
  );

  const renderStepThree = () => {
    if (!importResult) {
      return (
        <p className="bsds-text-regular text-sm text-gray-600" role="status">
          Nenhum resultado disponível.
        </p>
      );
    }

    if (importResult.kind === 'error') {
      const errors = importResult.errors ?? [];

      return (
        <>
          <div
            id={resultsSectionId}
            className="bsds-mb-16"
            role="region"
            aria-label="Erros na importação"
          >
            <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
              <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
              <h4 className="opacity-80 bsds-mb-0">Erros na importação</h4>
            </div>

            <p className="text-sm text-red-700 bsds-mb-16" role="alert">
              {importResult.message}
            </p>

            {errors.length > 0 && (
              <div className="w-full overflow-x-auto custom-scrollbar rounded-md shadow-xs">
                <table
                  className="min-w-[720px] w-full table-fixed"
                  aria-label="Tabela de críticas da importação"
                >
                  <thead>
                    <tr>
                      <th
                        className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-24"
                        scope="col"
                      >
                        Linha
                      </th>
                      <th
                        className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-48"
                        scope="col"
                      >
                        Campo
                      </th>
                      <th
                        className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap"
                        scope="col"
                      >
                        Crítica
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white divide-y divide-gray-50 text-text-color">
                    {errors.map((error) => (
                      <tr
                        key={`${error.linha}-${error.campo}-${error.critica}`}
                        className="hover:bg-gray-50 transition-colors text-xs"
                      >
                        <td className="px-4 py-2 whitespace-nowrap">{error.linha}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{error.campo}</td>
                        <td className="px-4 py-2">{error.critica}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="bsds-display-flex flex-col sm:flex-row gap-4 bsds-mt-24 bsds-pt-8">
            <Button
              type="button"
              color="secondary"
              fill="outline"
              className="bsds-width-100 sm:w-auto"
              onClick={handleBackToStepOne}
              aria-label="Voltar para ajustar importação"
            >
              Voltar
            </Button>

            <Button
              type="button"
              color="primary"
              fill="solid"
              className="bsds-width-100 sm:w-auto sm:ml-auto"
              onClick={handleResetImport}
              aria-label="Iniciar nova importação"
            >
              Nova importação
            </Button>
          </div>
        </>
      );
    }

    return (
      <>
        <div role="region" aria-label="Resultado da importação">
          <p className="text-sm text-green-700 bsds-mb-16" role="status">
            {importResult.message}
          </p>
          <p className="text-sm text-gray-600">
            A listagem de proponentes será atualizada ao concluir.
          </p>
        </div>

        <div className="bsds-display-flex flex-col sm:flex-row gap-4 bsds-mt-8">
          <Button
            type="button"
            color="secondary"
            fill="outline"
            className="bsds-width-100 sm:w-auto"
            onClick={handleResetImport}
            aria-label="Iniciar nova importação"
          >
            Nova importação
          </Button>

          <Button
            type="button"
            color="primary"
            fill="solid"
            className="bsds-width-100 sm:w-auto sm:ml-auto"
            onClick={onCancel}
            aria-label="Concluir e voltar para lista de proponentes"
          >
            Concluir
          </Button>
        </div>
      </>
    );
  };

  return (
    <section
      className="bsds-mt-24 bsds-mb-24"
      aria-label="Importar proponentes em lote"
    >
      <button
        type="button"
        className="bsds-display-flex bsds-align-items-center cursor-pointer text-gray-700 bsds-mb-16 border-0 bg-transparent p-0 mb-8"
        onClick={onCancel}
        aria-label="Voltar para lista de proponentes"
      >
        <BsdsIcon
          icon={iconSetaDuplaEsquerda}
          className="w-4 h-4 text-gray-700 shrink-0 bsds-mr-8"
          aria-hidden="true"
        />
        Voltar
      </button>

      <StepperX totalSteps={IMPORT_TOTAL_STEPS} currentStep={currentStep} />

      <p
        className="text-sm font-semibold text-gray-700 text-center bsds-mb-16"
        aria-live="polite"
      >
        Passo {currentStep} de {IMPORT_TOTAL_STEPS}: {IMPORT_STEP_LABELS[currentStep]}
      </p>

      {subestipulante && (
        <BsdsCard
          className="grid grid-cols-1 md:grid-cols-3 gap-4 bsds-p-16 bsds-mb-16 bg-gray-100/25 shadow-none rounded-sm"
          role="region"
          aria-label="Subestipulante da importação"
        >
          <div>
            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Sub-Estipulante</span>
            <span className="bsds-text-regular font-semibold">
              {subestipulante.nomeSubestipulanteOut?.trim() || '—'}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">ID</span>
            <span className="bsds-text-regular font-semibold">
              {subestipulante.identSubestipulanteOut ?? '—'}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-600 bsds-mb-4 bsds-display-block">Documento</span>
            <span className="bsds-text-regular font-semibold">
              {formatDocSubestipulante(subestipulante.numDocumentoOut)}
            </span>
          </div>
        </BsdsCard>
      )}

      <BsdsCard className="bsds-p-16 bsds-mt-8 border border-dashed border-gray-200">
        <input
          ref={fileInputRef}
          id={fileInputId}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="sr-only"
          onChange={handleInputChange}
          aria-hidden="true"
          tabIndex={-1}
        />

        {currentStep === 1 && renderStepOne()}
        {currentStep === 2 && renderStepTwo()}
        {currentStep === 3 && renderStepThree()}
      </BsdsCard>

      {currentStep === 1 && (
        <BsdsCard className="bsds-p-16 bsds-mt-16 bg-gray-100/25 shadow-none">
          <div className="bsds-display-flex bsds-align-items-center bsds-mb-8">
            <BsdsIcon
              icon={iconAlertaAtencao}
              className="w-4 h-4 text-gray-400 bsds-mr-8 shrink-0"
              aria-hidden="true"
            />

            <h6 className="text-sm font-semibold text-gray-800 bsds-mb-0">
              Formato esperado
            </h6>
          </div>

          <ul className="text-sm text-gray-600 space-y-1 list-disc bsds-pl-16">
            <li>Extensões aceitas: XLSX, XLS e CSV</li>
            <li>Tamanho máximo: 20 MB</li>
          </ul>
        </BsdsCard>
      )}
    </section>
  );
};

export default ImportProponentesBatchSection;
